<script setup lang="ts">
/**
 * The way between two stops of a journey timeline: a solid rail for a ride, a dotted one for
 * a walk. `current` puts the traveller's position on it, `past` greys it out.
 */
withDefaults(
  defineProps<{
    mode: 'ride' | 'walk';
    state?: 'default' | 'past' | 'current';
    /** Color of the rail of a ride, the line's color */
    color?: string;
  }>(),
  { state: 'default', color: undefined },
);
</script>

<template>
  <div
    class="timeline-segment"
    :class="[`timeline-segment--${mode}`, `timeline-segment--${state}`]"
    :style="color ? { '--timeline-segment-color': color } : undefined"
    role="listitem"
  >
    <span class="timeline-segment__time" />
    <span class="timeline-segment__rail" aria-hidden="true">
      <span class="timeline-segment__line" />
      <span v-if="state === 'current'" class="timeline-segment__position" />
    </span>
    <div class="timeline-segment__content">
      <slot />
    </div>
  </div>
</template>

<style lang="scss">
.timeline-segment {
  --timeline-segment-rail: var(--timeline-segment-color, #{$color-neutral-highest});

  display: flex;
  gap: $spacing-2;

  &__time {
    flex-shrink: 0;
    width: max(44px, var(--timeline-time-width, 0px));
  }

  &__rail {
    align-self: stretch;
    flex-shrink: 0;
    position: relative;
    width: 20px;
  }

  &__line {
    bottom: 0;
    left: 8px;
    position: absolute;
    top: 0;
    width: 4px;
  }

  &__position {
    background-color: $color-geolocation;
    border: 2.5px solid $color-white;
    border-radius: 50%;
    box-shadow: $shadow-bus;
    box-sizing: border-box;
    height: 20px;
    left: 0;
    position: absolute;
    top: calc(50% - 10px);
    width: 20px;
  }

  &__content {
    display: flex;
    flex: 1;
    flex-direction: column;
    gap: $spacing-1;
    min-width: 0;
    padding: 2px 0 14px;
  }

  &--ride .timeline-segment__line {
    background-color: var(--timeline-segment-rail);
  }

  // 4px dots every 8px, the first one 2px down
  &--walk .timeline-segment__line {
    background: radial-gradient(circle at 2px 4px, var(--timeline-segment-dots) 1.5px, transparent 2px) top /
      4px 8px repeat-y;
  }

  &--walk {
    --timeline-segment-dots: #{$color-neutral-higher};
  }

  &--past {
    --timeline-segment-rail: #{$color-neutral-high};
    --timeline-segment-dots: #{$color-neutral-high};
  }
}
</style>
